"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState, useSyncExternalStore, type CSSProperties } from "react";
import { Accordion } from "@repo/ui/mantine";
import { Typography } from "@repo/ui/aops-components/typography";
import { cmsBrandColorCssVar } from "@repo/ui/lib/cmsBrandColors";
import { cn } from "@repo/ui/lib/utils";
import { SanityMediaFieldContent } from "@repo/sanity-utils/components/media/SanityMediaFieldContent";
import type { DiscoveryPageQueryResult } from "@repo/sanity-utils/schema/types";
import { Minus, Plus } from "lucide-react";
import type { ResolvedSanityImage } from "@/lib/discoveryMedia";
import { AopsPortableText } from "../../PortableText/PortableTextComponents";
import styles from "./DiscoveryAccordionBlock.module.css";
type DiscoveryPageBlocks = NonNullable<NonNullable<DiscoveryPageQueryResult>["pageContent"]>;
export type DiscoveryAccordionBlockData = Extract<DiscoveryPageBlocks[number], {
  _type: "discoveryAccordionBlock";
}>;
type AccordionItem = NonNullable<DiscoveryAccordionBlockData["items"]>[number];
export type DiscoveryAccordionItemMedia = {
  imageData?: ResolvedSanityImage;
  videoUrl?: string | null;
  alt?: string;
};
type DiscoveryAccordionBlockProps = {
  block: DiscoveryAccordionBlockData;
  mediaByKey: Record<string, DiscoveryAccordionItemMedia>;
};
type PlayMode = "idle" | "auto" | "manual" | "done";
type AccentBarStyle = CSSProperties & {
  "--section-duration"?: string;
};
const DEFAULT_SECTION_DURATION_MS = 5000;
const DESKTOP_MEDIA_QUERY = "(min-width: 1024px)";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const EMPTY_ITEMS: AccordionItem[] = [];
function subscribeToMediaQuery(query: string, onStoreChange: () => void) {
  const mediaQuery = window.matchMedia(query);
  mediaQuery.addEventListener("change", onStoreChange);
  return () => mediaQuery.removeEventListener("change", onStoreChange);
}
function useMediaQuery(query) {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] !== query) {
    t0 = onStoreChange => subscribeToMediaQuery(query, onStoreChange);
    t1 = () => window.matchMedia(query).matches;
    $[0] = query;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  return useSyncExternalStore(t0, t1, _temp);
}
function _temp() {
  return false;
}
function getDefaultOpenKey(items: AccordionItem[]): string {
  const defaultItem = items.find(item => item.defaultOpen);
  return defaultItem?._key ?? items[0]?._key ?? "";
}
function AccordionItemMedia(t0) {
  const $ = _c(12);
  const {
    media,
    className,
    sizes
  } = t0;
  if (!media?.imageData && !media?.videoUrl) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = cn(styles.mediaFrame, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== media.alt || $[3] !== media.imageData) {
    t2 = media.imageData ? {
      src: media.imageData.url,
      alt: media.alt ?? "",
      width: media.imageData.width,
      height: media.imageData.height
    } : null;
    $[2] = media.alt;
    $[3] = media.imageData;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = media.imageData ? null : media.videoUrl ?? null;
  let t4;
  if ($[5] !== sizes || $[6] !== t2 || $[7] !== t3) {
    t4 = <SanityMediaFieldContent image={t2} videoUrl={t3} imageClassName={styles.mediaImage} videoClassName={styles.mediaVideo} sizes={sizes} fill={true} />;
    $[5] = sizes;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t4) {
    t5 = <div className={t1}>{t4}</div>;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
export function DiscoveryAccordionBlock(t0) {
  const $ = _c(56);
  const {
    block,
    mediaByKey
  } = t0;
  const items = block.items ?? EMPTY_ITEMS;
  const sectionRef = useRef(null);
  let t1;
  if ($[0] !== items) {
    t1 = () => getDefaultOpenKey(items);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [opened, setOpened] = useState(t1);
  const [mode, setMode] = useState("idle");
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);
  const prefersReducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);
  const canAutoplay = isDesktop && !prefersReducedMotion;
  const durationMs = typeof block.sectionDurationMs === "number" && block.sectionDurationMs > 0 ? block.sectionDurationMs : DEFAULT_SECTION_DURATION_MS;
  let t2;
  let t3;
  if ($[2] !== canAutoplay || $[3] !== mode) {
    t2 = () => {
      if (mode !== "idle" || !canAutoplay) {
        return;
      }
      const el = sectionRef.current;
      if (!el) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        if (entries.some(_temp2)) {
          setMode("auto");
          observer.disconnect();
        }
      }, {
        threshold: 0.75
      });
      observer.observe(el);
      return () => observer.disconnect();
    };
    t3 = [mode, canAutoplay];
    $[2] = canAutoplay;
    $[3] = mode;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[6] !== canAutoplay || $[7] !== durationMs || $[8] !== items || $[9] !== mode || $[10] !== opened) {
    t4 = () => {
      if (mode !== "auto" || !canAutoplay) {
        return;
      }
      const timeoutId = window.setTimeout(() => {
        const currentIndex = items.findIndex(item => item._key === opened);
        if (currentIndex < 0) {
          return;
        }
        if (currentIndex >= items.length - 1) {
          setMode("done");
          return;
        }
        const nextItem = items[currentIndex + 1];
        if (nextItem) {
          setOpened(nextItem._key);
        }
      }, durationMs);
      return () => window.clearTimeout(timeoutId);
    };
    t5 = [mode, opened, canAutoplay, durationMs, items];
    $[6] = canAutoplay;
    $[7] = durationMs;
    $[8] = items;
    $[9] = mode;
    $[10] = opened;
    $[11] = t4;
    $[12] = t5;
  } else {
    t4 = $[11];
    t5 = $[12];
  }
  useEffect(t4, t5);
  if (items.length === 0) {
    return null;
  }
  let t6;
  if ($[13] !== items || $[14] !== opened) {
    t6 = opened || getDefaultOpenKey(items);
    $[13] = items;
    $[14] = opened;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const openKey = t6;
  const openMedia = mediaByKey[openKey];
  const barIsAnimating = mode === "auto" && canAutoplay;
  const barIsFilled = mode === "manual" || mode === "done" || prefersReducedMotion;
  let t7;
  if ($[16] !== block.eyebrow) {
    t7 = block.eyebrow ? <Typography variant="eyebrow" as="p" className={styles.eyebrow}>{block.eyebrow}</Typography> : null;
    $[16] = block.eyebrow;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== block.title) {
    t8 = block.title ? <><Typography variant="h4" as="h2" className={cn(styles.title, styles.titleMobile)}>{block.title}</Typography><Typography variant="h3" as="span" className={cn(styles.title, styles.titleTablet)} aria-hidden={true}>{block.title}</Typography><Typography variant="h2" as="span" className={cn(styles.title, styles.titleDesktop)} aria-hidden={true}>{block.title}</Typography></> : null;
    $[18] = block.title;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t7 || $[21] !== t8) {
    t9 = <header className={styles.header}>{t7}{t8}</header>;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const t10 = openMedia == null;
  let t11;
  if ($[23] !== openMedia) {
    t11 = <AccordionItemMedia media={openMedia} sizes="(min-width: 1024px) 50vw, 0px" />;
    $[23] = openMedia;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11) {
    t12 = <div className={styles.sideMedia} aria-hidden={t10}>{t11}</div>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = value => {
      if (value) {
        setOpened(value);
        setMode("manual");
      }
    };
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      root: styles.accordionRoot,
      item: styles.accordionItem,
      control: styles.accordionControl,
      label: styles.accordionLabel,
      panel: styles.accordionPanel,
      content: styles.accordionContent
    };
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== barIsAnimating || $[31] !== barIsFilled || $[32] !== durationMs || $[33] !== items || $[34] !== mediaByKey || $[35] !== mode || $[36] !== openKey || $[37] !== opened) {
    let t16;
    if ($[39] !== barIsAnimating || $[40] !== barIsFilled || $[41] !== durationMs || $[42] !== mediaByKey || $[43] !== mode || $[44] !== openKey || $[45] !== opened) {
      t16 = item_0 => {
        const isOpen = item_0._key === openKey;
        const accent = cmsBrandColorCssVar(item_0.accentColor) ?? "var(--color-surface-accent-yellow)";
        const itemMedia = mediaByKey[item_0._key];
        const accentBarStyle = {
          backgroundColor: accent,
          "--section-duration": `${durationMs}ms`
        };
        return <Accordion.Item key={item_0._key} value={item_0._key} className={cn(isOpen && styles.accordionItemOpen)}>{isOpen ? <div key={`${item_0._key}-${barIsAnimating ? opened : mode}`} className={cn(styles.accentBar, barIsAnimating && styles.accentBarAnimating, barIsFilled && styles.accentBarFilled)} style={accentBarStyle} aria-hidden={true} /> : null}<Accordion.Control><span className={styles.controlInner}><Typography variant="h5" as="span" className={cn(styles.itemTitle, styles.itemTitleCompact)}>{item_0.title}</Typography><Typography variant="h4" as="span" className={cn(styles.itemTitle, styles.itemTitleDesktop)} aria-hidden={true}>{item_0.title}</Typography><span className={styles.chevron} aria-hidden={true}>{isOpen ? <Minus size={24} strokeWidth={1.5} /> : <Plus size={24} strokeWidth={1.5} />}</span></span></Accordion.Control><Accordion.Panel>{Array.isArray(item_0.body) && item_0.body.length > 0 ? <div className={styles.itemBody}><AopsPortableText value={item_0.body} /></div> : null}<AccordionItemMedia media={itemMedia} className={styles.inlineMedia} sizes="(max-width: 1023px) 100vw, 0px" /></Accordion.Panel></Accordion.Item>;
      };
      $[39] = barIsAnimating;
      $[40] = barIsFilled;
      $[41] = durationMs;
      $[42] = mediaByKey;
      $[43] = mode;
      $[44] = openKey;
      $[45] = opened;
      $[46] = t16;
    } else {
      t16 = $[46];
    }
    t15 = items.map(t16);
    $[30] = barIsAnimating;
    $[31] = barIsFilled;
    $[32] = durationMs;
    $[33] = items;
    $[34] = mediaByKey;
    $[35] = mode;
    $[36] = openKey;
    $[37] = opened;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[47] !== openKey || $[48] !== t15) {
    t16 = <Accordion variant="unstyled" value={openKey} onChange={t13} chevron={null} classNames={t14}>{t15}</Accordion>;
    $[47] = openKey;
    $[48] = t15;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== t12 || $[51] !== t16) {
    t17 = <div className={styles.panel}>{t12}{t16}</div>;
    $[50] = t12;
    $[51] = t16;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  let t18;
  if ($[53] !== t17 || $[54] !== t9) {
    t18 = <div ref={sectionRef} className={styles.section}>{t9}{t17}</div>;
    $[53] = t17;
    $[54] = t9;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  return t18;
}
function _temp2(entry) {
  return entry.isIntersecting;
}